import { Helmet } from '@dr.pogodin/react-helmet';
import {
  skipJaneDescription,
  skipJaneTitle,
  useRuntimeConfig,
} from '@iheartjane/runtime-config';
import type { AppMode } from '@iheartjane/types';

// eslint-disable-next-line no-restricted-imports
import type { Store } from '@jane/shared/models';
import type { EcommPaths } from '@jane/shared/types';
import { appendDispensaryToStoreName } from '@jane/shared/util';

import { getCanonicalPath } from './getCanonicalPath';
import { OpenGraph } from './openGraph';

const generateMetaDescription = ({
  name,
  metro_area,
  delivery,
  recreational,
  medical,
}: Pick<
  Store,
  'name' | 'metro_area' | 'delivery' | 'recreational' | 'medical'
>) => {
  let storeType = 'medical and recreational';
  let fulfillmentType = 'pickup';

  if (medical && !recreational) {
    storeType = 'medical';
  }

  if (recreational && !medical) {
    storeType = 'recreational';
  }

  if (delivery) {
    fulfillmentType = 'delivery and pickup';
  }

  return `Visit ${appendDispensaryToStoreName(name, "'s dispensary")} in ${metro_area} and order ${storeType} cannabis online for ${fulfillmentType}. Browse our online dispensary menu for flower, edibles, vape and more.`;
};

export const generateStoreDetailTitle = ({
  name,
  metro_area,
}: Pick<Store, 'name' | 'metro_area'>) =>
  `Shop featured cannabis products | ${appendDispensaryToStoreName(name, ' Dispensary')} ${metro_area}`;

export const StoreDetailMetaData = ({
  appMode,
  isIframe,
  paths,
  store,
}: {
  appMode: AppMode;
  isIframe: boolean;
  paths: EcommPaths;
  store: Store;
}) => {
  const runtimeConfig = useRuntimeConfig();
  const { isBoostForAll, isPartnerHosted } = runtimeConfig;

  if (!store) {
    return null;
  }

  const title = generateStoreDetailTitle(store);
  const description = generateMetaDescription(store);

  const url = getCanonicalPath({
    inIframe: isIframe,
    isBoostWp: runtimeConfig.isFramelessEmbedMode && !isBoostForAll,
    pagePath: paths.menu(),
  });

  const skipTitle = skipJaneTitle(runtimeConfig);
  const skipDescription = skipJaneDescription(runtimeConfig);
  const disableIndexing = appMode === 'default' && !store.visible;

  return (
    <>
      <Helmet>
        {!skipTitle && <title>{title}</title>}
        {!skipDescription && <meta name="description" content={description} />}

        {/** Url & product detail metadata is set in boost4all controller https://github.com/janetechinc/iheartjane/blob/b36d47cefe38578e391ecdd3d33addf12bd1f425/api/app/lib/boost/compilation/compile.rb#L35 */}
        {!isBoostForAll && <link rel="canonical" href={url} />}

        {disableIndexing && <meta name="robots" content="noindex,nofollow" />}
      </Helmet>
      <OpenGraph
        title={title}
        description={description}
        url={url}
        isPartnerHosted={isPartnerHosted}
        isBoostForAll={isBoostForAll}
        skipDescription={skipDescription}
        skipTitle={skipTitle}
      />
    </>
  );
};
